import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { useSalePermissions } from "../../sale/hooks/use-sale-permissions.js";
import {
  parseSaleReopenNotificationDeeplink,
  useSaleReopen,
} from "../../sale/reopen/index.js";
import { useMarkNotificationAsRead } from "../hooks/use-mark-notification-as-read.js";
import { useNotifications } from "../hooks/use-notifications.js";
import { sortNotificationsByCreatedAtDesc } from "../lib/sort-notifications-by-created-at-desc.js";
import { NotificationBell } from "./notification-bell.js";
import { NotificationsSidebar } from "./notifications-sidebar.js";

/**
 * Feature Logic: inbox + mark-as-read + ações approve/reject no card
 * para `notification.sale.reopen-requested`.
 */
export function NotificationsInbox() {
  const [isOpen, setIsOpen] = useState(false);
  const [markingId, setMarkingId] = useState(null);
  const [reopenBusyId, setReopenBusyId] = useState(null);

  const { notifications, unreadCount, isLoading, error, reload } = useNotifications();
  const { markAsRead } = useMarkNotificationAsRead();
  const { canReopen } = useSalePermissions();
  const { approveReopen, rejectReopen } = useSaleReopen();

  const sortedNotifications = useMemo(
    () => sortNotificationsByCreatedAtDesc(notifications),
    [notifications],
  );

  const notificationsById = useMemo(() => {
    /** @type {Map<string, Object>} */
    const map = new Map();
    for (const item of notifications) {
      if (item?.id) map.set(item.id, item);
    }
    return map;
  }, [notifications]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleRetry = useCallback(async () => {
    const response = await reload();
    if (!response?.success) {
      toast.error(response?.message || "Erro ao buscar notificações");
    }
  }, [reload]);

  const resolveReopenContext = useCallback((notification) => {
    const parsed = parseSaleReopenNotificationDeeplink(
      notification?.deeplink || "",
    );
    const saleId =
      notification?.saleId || parsed.saleId || null;
    const notifyUserId =
      notification?.requesterUserId ?? parsed.notifyUserId ?? null;
    return { saleId, notifyUserId };
  }, []);

  const handleMarkRead = useCallback(
    async (notificationId) => {
      const notification = notificationsById.get(notificationId);
      if (!notification || notification.requiresAction) return;

      setMarkingId(notificationId);
      try {
        const response = await markAsRead(notificationId);
        if (!response?.success) {
          toast.error(
            response?.message || "Erro ao marcar notificação como lida",
          );
        }
      } finally {
        setMarkingId(null);
      }
    },
    [markAsRead, notificationsById],
  );

  const handleOpenNotification = useCallback(
    async (notificationId) => {
      const notification = notificationsById.get(notificationId);
      if (!notification) return;

      if (!notification.requiresAction) {
        await handleMarkRead(notificationId);
      }
      setIsOpen(false);
    },
    [handleMarkRead, notificationsById],
  );

  const handleApproveReopen = useCallback(
    async (notificationId) => {
      const notification = notificationsById.get(notificationId);
      if (!notification) return;

      const { saleId, notifyUserId } = resolveReopenContext(notification);
      if (!saleId || notifyUserId == null) {
        toast.error(
          "Não foi possível aprovar: saleId ou notifyUserId ausente.",
        );
        return;
      }

      setReopenBusyId(notificationId);
      try {
        const response = await approveReopen(saleId, { notifyUserId });
        if (!response?.success) {
          toast.error(response?.message || "Erro ao aprovar reabertura");
          return;
        }
        toast.success(response?.message || "Venda reaberta com sucesso.");
        await markAsRead(notificationId);
        await reload();
      } finally {
        setReopenBusyId(null);
      }
    },
    [
      approveReopen,
      markAsRead,
      notificationsById,
      reload,
      resolveReopenContext,
    ],
  );

  const handleRejectReopen = useCallback(
    async (notificationId, reason) => {
      const notification = notificationsById.get(notificationId);
      if (!notification) return;

      const { saleId, notifyUserId } = resolveReopenContext(notification);
      if (!saleId || notifyUserId == null) {
        toast.error(
          "Não foi possível recusar: saleId ou notifyUserId ausente.",
        );
        return;
      }

      setReopenBusyId(notificationId);
      try {
        const response = await rejectReopen(saleId, {
          notifyUserId,
          reason,
        });
        if (!response?.success) {
          toast.error(response?.message || "Erro ao recusar reabertura");
          return;
        }
        toast.success(
          response?.message || "Solicitação de reabertura recusada.",
        );
        await markAsRead(notificationId);
        await reload();
      } finally {
        setReopenBusyId(null);
      }
    },
    [
      markAsRead,
      notificationsById,
      rejectReopen,
      reload,
      resolveReopenContext,
    ],
  );

  return (
    <>
      <NotificationBell count={unreadCount} onClick={handleOpen} />
      <NotificationsSidebar
        isOpen={isOpen}
        onClose={handleClose}
        notifications={sortedNotifications}
        isLoading={isLoading}
        error={error}
        onRetry={handleRetry}
        onOpenNotification={handleOpenNotification}
        onMarkReadNotification={handleMarkRead}
        markingId={markingId}
        canReopen={canReopen}
        reopenBusyId={reopenBusyId}
        onApproveReopen={handleApproveReopen}
        onRejectReopen={handleRejectReopen}
      />
    </>
  );
}
